import { Store } from '@ngrx/store';
import { select } from '@shared/datastore/operators/select';
import { Observable } from 'rxjs/Observable';
import { combineLatest, distinctUntilChanged, filter, map, pairwise, shareReplay, switchMap, take, tap, } from 'rxjs/operators';
import { ErrorTracking } from '../tracking';
import { ApiHttp } from './api-http.service';
import { arrayIsShallowEqual, isDefined, objectFilterByKeys, sameElements, setDiff, toNumber, toObservable, } from './helpers';
import { ListObservable } from './list-observable';
import { ObjectObservable } from './object-observable';
import { duplicateFirst } from './operators/duplicateFirst';
import { Query } from './query';
import { transformSearch } from './search/search.transformers';
import { stringifyQuery } from './store.helpers';
import { WebSocketService } from './websocket';
var Datastore = /** @class */ (function () {
    function Datastore(store, apiHttp, webSocketService, errorTracking) {
        this.store = store;
        this.apiHttp = apiHttp;
        this.webSocketService = webSocketService;
        this.errorTracking = errorTracking;
    }
    Datastore.prototype.list = function (key1, key2, queryFn) {
        var _this = this;
        var rawQuery = queryFn ? queryFn(new Query()).query : {};
        var refStream = Observable.combineLatest([
            toObservable(key1),
            toObservable(key2),
            toObservable(rawQuery.limit),
            toObservable(rawQuery.offset),
            toObservable(rawQuery.params),
        ], function (path1, path2, limit, offset, params) {
            var path = [path1, path2, undefined];
            return { path: path, query: { limit: limit, offset: offset, params: params } };
        }).pipe(tap(function (ref) {
            var action = {
                type: 'REQUEST_DATA',
                payload: {
                    type: ref.path[0],
                    ref: ref,
                },
            };
            _this.store.dispatch(action);
        }), shareReplay(1));
        return new ListObservable(refStream, this.apiHttp, this.store, this.errorTracking, refStream.pipe(switchMap(function (_a) {
            var _b = _a.path, path1 = _b[0], path2 = _b[1], query = _a.query;
            return _this.store.pipe(select(path1, path2), map(function (data) {
                var queryString = stringifyQuery(query);
                if (data && data.lists && data.lists[queryString]) {
                    return ((data.lists[queryString] || { ids: [] }).ids || [])
                        .slice((query && query.offset) || 0)
                        .map(function (id) { return data.entities[id].entity; });
                }
                return undefined;
            }), filter(isDefined), distinctUntilChanged(arrayIsShallowEqual));
        })));
    };
    /* Returns an observable with a single object.
     */
    Datastore.prototype.object = function (key1, key2, objectId) {
        var _this = this;
        var refStream = Observable.combineLatest([
            toObservable(key1),
            toObservable(key2),
            toObservable(objectId).pipe(distinctUntilChanged()),
        ], function (path1, path2, id) {
            var path = [path1, path2, [id.toString()]];
            return { path: path };
        }).pipe(switchMap(function (_a) {
            var _b = _a.path, path1 = _b[0], path2 = _b[1];
            return _this.store.pipe(select(path1, path2), take(1));
        }, function (_a, data) {
            var path = _a.path;
            return ({ ref: { path: path }, data: data });
        }), tap(function (_a) {
            var ref = _a.ref, data = _a.data;
            var _b = ref.path, ids = _b[2];
            // Don't fetch if this was fetched in the last second.
            if (!data ||
                !data.entities[ids[0]] ||
                toNumber(data.entities[ids[0]].timeFetched) + 1000 < Date.now()) {
                var action = {
                    type: 'REQUEST_DATA',
                    payload: {
                        type: ref.path[0],
                        ref: ref,
                    },
                };
                _this.store.dispatch(action);
            }
        }), map(function (_a) {
            var ref = _a.ref;
            return ref;
        }), shareReplay(1));
        var subscriptionStream = key1 === 'onlineOffline'
            ? this.webSocketService
                .subscribeOnlineOffline(refStream.pipe(map(function (_a) {
                var _b = _a.path, path1 = _b[0], path2 = _b[1], id = _b[2][0];
                return parseInt(id, 10);
            })))
                .pipe(combineLatest(refStream, function (ss, ref) { return ref; }))
            : refStream;
        return new ObjectObservable(Observable.merge(refStream, subscriptionStream).pipe(switchMap(function (_a) {
            var _b = _a.path, path1 = _b[0], path2 = _b[1], ids = _b[2];
            return _this.store.pipe(select(path1, path2), filter(isDefined), map(function (obj) { return obj.entities[ids[0]] && obj.entities[ids[0]].entity; }), filter(isDefined), distinctUntilChanged());
        })));
    };
    /* Returns a list of objects at a location.
     * If an observable is passed in for key2 then only the delta will be
     * refetched from the network.
     */
    Datastore.prototype.objects = function (key1, key2, objectIds) {
        var _this = this;
        var refStream = Observable.combineLatest([
            toObservable(key1),
            toObservable(key2),
            toObservable(objectIds).pipe(distinctUntilChanged(arrayIsShallowEqual)),
        ], function (path1, path2, ids) {
            var path = [
                path1,
                path2,
                ids.map(function (id) { return id.toString(); }),
            ];
            return { path: path };
        }).pipe(
        // Add an path at the start with an empty list of ids
        duplicateFirst(function (_a) {
            var _b = _a.path, path1 = _b[0], path2 = _b[1];
            return ({ path: [path1, path2, []] });
        }), pairwise(), // Use pairwise and only fetch new things
        tap(function (_a) {
            var _b = _a[0].path, idsOrig = _b[2], _c = _a[1].path, type = _c[0], path2 = _c[1], idsNew = _c[2];
            var newIds = setDiff(idsNew, idsOrig);
            if (newIds.length) {
                var action = {
                    type: 'REQUEST_DATA',
                    payload: { type: type, ref: { path: [type, path2, newIds] } },
                };
                _this.store.dispatch(action);
            }
        }), map(function (_a) {
            var path1 = _a[0].path, path2 = _a[1].path;
            return ({ path: path2 });
        }), shareReplay(1));
        return refStream.pipe(switchMap(function (_a) {
            var _b = _a.path, path1 = _b[0], path2 = _b[1], ids = _b[2];
            return _this.store.pipe(select(path1, path2), filter(isDefined), map(function (entitiesObject) {
                return objectFilterByKeys(entitiesObject.entities, ids);
            }), map(function (entitiesObject) { return Object.values(entitiesObject); }), map(function (entities) { return entities.map(function (entity) { return entity.entity; }); }), distinctUntilChanged(sameElements));
        }));
    };
    // Hits an elastic search endpoint directly and returns the result.
    // The data doesn't get stored in the datastore (at least yet).
    Datastore.prototype.search = function (key, query, pagination) {
        return this.apiHttp
            .fetchSearch(key, toObservable(query))
            .pipe(map(function (result) { return transformSearch(key, result); }));
    };
    return Datastore;
}());
export { Datastore };



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/datastore.ts.pre-build-optimizer.js